import { createApi } from '@reduxjs/toolkit/query/react';

import { IComment } from '@/interfaces/comments';
import { IPaginationParams, IPaginationResponse } from '@/interfaces/pagination';

import { axiosBaseQuery } from './query';

type AdditionalFilters = { allComments: boolean; ownComments: boolean };

export const commentsApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'commentsApi',
  tagTypes: ['list'],
  endpoints(build) {
    return {
      deleteComment: build.mutation<IComment, { lessonId: string | undefined; isReply: boolean; commentId: number }>({
        invalidatesTags: ['list'],
        query: ({ lessonId, commentId, isReply }) => ({
          formatResponse: response => response.data.data,
          url: `/learner/lessons/${lessonId}/${isReply ? 'reply' : 'comments'}/${commentId}`,
          method: 'DELETE'
        })
      }),
      saveReply: build.mutation<IComment, { lessonId: string | undefined; parentId: number; comment: string }>({
        invalidatesTags: ['list'],
        query: ({ lessonId, comment, parentId }) => ({
          formatResponse: response => response.data.data,
          url: `/learner/lessons/${lessonId}/comments/${parentId}/reply`,
          method: 'POST',
          data: {
            text: comment
          }
        })
      }),
      saveComment: build.mutation<IComment, { lessonId: string | undefined; comment: string }>({
        invalidatesTags: ['list'],
        query: ({ lessonId, comment }) => ({
          formatResponse: response => response.data.data,
          url: `/learner/lessons/${lessonId}/comments`,
          method: 'POST',
          data: {
            text: comment
          }
        })
      }),
      getReplies: build.query<
        IPaginationResponse<IComment[]>,
        { lessonId: string | undefined; parentId: number; params: Partial<IPaginationParams> }
      >({
        forceRefetch({ currentArg, previousArg }) {
          return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
        },
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
        merge: (currCache, newCache) => {
          currCache.total_rows = newCache.total_rows;
          currCache.total_pages = newCache.total_pages;
          currCache.page = newCache.page;
          currCache.links = newCache.links;

          if (newCache.page.page === 1) {
            currCache.data = newCache.data;
          } else {
            currCache.data.push(...newCache.data);
          }
        },
        query: ({ lessonId, parentId }) => ({
          url: `/learner/lessons/${lessonId}/comments/${parentId}?order=ASC&reply=true`,
          method: 'GET'
        })
      }),
      getComments: build.query<
        IPaginationResponse<IComment[], AdditionalFilters>,
        { lessonId: string | undefined; params: Partial<IPaginationParams & AdditionalFilters> }
      >({
        providesTags: ['list'],
        forceRefetch({ currentArg, previousArg }) {
          return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
        },
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
        merge: (currCache, newCache) => {
          currCache.total_rows = newCache.total_rows;
          currCache.total_pages = newCache.total_pages;
          currCache.page = newCache.page;
          currCache.links = newCache.links;

          if (newCache.page.page === 1) {
            currCache.data = newCache.data;
          } else {
            currCache.data.push(...newCache.data);
          }
        },
        query: ({ lessonId, params }) => ({
          url: `/learner/lessons/${lessonId}/comments`,
          params
        })
      })
    };
  }
});

export const { useGetCommentsQuery, useSaveCommentMutation, useSaveReplyMutation, useDeleteCommentMutation } =
  commentsApi;
